import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Check, Download, Sparkles, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const LOCALIZED_FALLBACK_DIETS = {
  en: [
    {
      id: 'diet_mediterranean',
      title: '1-Week Mediterranean Diet Plan',
      description:
        'A heart-healthy 7-day meal plan focusing on whole grains, healthy fats, and lean proteins.',
      price_eur: 9.99,
      badge: 'Best Seller',
      features: [
        '7 daily meal plans with recipes',
        'Complete grocery list',
        'Meal prep guide',
      ],
    },
    {
      id: 'diet_lowcarb',
      title: '1-Week Low-Carb Diet Plan',
      description:
        'Kickstart your metabolism with this delicious, easy-to-follow low-carbohydrate meal plan.',
      price_eur: 9.99,
      badge: 'Popular',
      features: [
        '7 daily meal plans with recipes',
        'Complete grocery list',
        'Macronutrient breakdown included',
      ],
    },
    {
      id: 'diet_plantbased',
      title: '1-Week Plant-Based Diet Plan',
      description:
        'A fully plant-based 7-day meal plan designed to provide optimal nutrition and steady energy.',
      price_eur: 9.99,
      badge: '100% Vegan',
      features: [
        '7 daily meal plans with recipes',
        'Complete grocery list',
        '100% plant-based & protein-balanced',
      ],
    },
  ],
  es: [
    {
      id: 'diet_mediterranean',
      title: 'Plan de Dieta Mediterránea de 1 Semana',
      description:
        'Plan de 7 días saludable para el corazón con cereales integrales, grasas saludables y proteína magra.',
      price_eur: 9.99,
      badge: 'Más Vendido',
      features: [
        '7 menús diarios con recetas paso a paso',
        'Lista de la compra completa',
        'Guía práctica de meal prep',
      ],
    },
    {
      id: 'diet_lowcarb',
      title: 'Plan Low-Carb de 1 Semana',
      description:
        'Activa tu metabolismo con este delicioso y sencillo plan bajo en carbohidratos.',
      price_eur: 9.99,
      badge: 'Popular',
      features: [
        '7 menús diarios con recetas',
        'Lista de la compra completa',
        'Desglose de macronutrientes incluido',
      ],
    },
    {
      id: 'diet_plantbased',
      title: 'Plan Plant-Based (Vegano) de 1 Semana',
      description:
        'Un plan 100% vegetal de 7 días diseñado para aportar nutrición óptima y energía constante.',
      price_eur: 9.99,
      badge: '100% Vegano',
      features: [
        '7 menús diarios con recetas',
        'Lista de la compra completa',
        '100% vegetal y equilibrado en proteínas',
      ],
    },
  ],
  it: [
    {
      id: 'diet_mediterranean',
      title: 'Piano Dieta Mediterranea di 1 Settimana',
      description:
        'Un piano di 7 giorni salutare per il cuore con cereali integrali, grassi sani e proteine magre.',
      price_eur: 9.99,
      badge: 'Più Venduto',
      features: [
        '7 menu giornalieri con ricette passo-passo',
        'Lista della spesa completa',
        'Guida pratica al meal prep',
      ],
    },
    {
      id: 'diet_lowcarb',
      title: 'Piano Low-Carb di 1 Settimana',
      description:
        'Attiva il tuo metabolismo con questo piano a basso contenuto di carboidrati, gustoso e semplice.',
      price_eur: 9.99,
      badge: 'Popolare',
      features: [
        '7 menu giornalieri con ricette',
        'Lista della spesa completa',
        'Suddivisione dei macronutrienti inclusa',
      ],
    },
    {
      id: 'diet_plantbased',
      title: 'Piano Plant-Based di 1 Settimana',
      description:
        'Un piano 100% vegetale di 7 giorni studiato per garantire nutrizione ottimale ed energia costante.',
      price_eur: 9.99,
      badge: '100% Vegano',
      features: [
        '7 menu giornalieri con ricette',
        'Lista della spesa completa',
        '100% vegetale e bilanciato nelle proteine',
      ],
    },
  ],
};

const Diets = () => {
  const { t, i18n } = useTranslation();
  const langCode = (i18n.language || 'en').split('-')[0];
  const fallbackForLang = LOCALIZED_FALLBACK_DIETS[langCode] || LOCALIZED_FALLBACK_DIETS.en;

  const [remoteDiets, setRemoteDiets] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [checkoutError, setCheckoutError] = useState('');

  useEffect(() => {
    const lang = (i18n.language || 'en').split('-')[0];
    fetch(`${API_URL}/api/diets?lang=${encodeURIComponent(lang)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setRemoteDiets(data);
        }
      })
      .catch(() => {});
  }, [i18n.language]);

  const diets = remoteDiets || fallbackForLang;

  const handlePaymentStripe = async (dietId) => {
    setCheckoutError('');
    setLoadingId(dietId);
    try {
      const response = await fetch(
        `${API_URL}/api/payments/create-diet-checkout?diet_id=${encodeURIComponent(dietId)}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setCheckoutError(data.detail || t('diets.errors.initFailed', 'Payment initialization failed.'));
        setLoadingId(null);
      }
    } catch {
      setCheckoutError(t('diets.errors.serverError', 'Error contacting the server. Please try again.'));
      setLoadingId(null);
    }
  };

  return (
    <div className="bg-gray-50/50 min-h-screen py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-primary/5 rounded-full filter blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[40rem] h-[40rem] bg-blue-400/5 rounded-full filter blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
            {t('diets.title', 'Ready-to-Follow Diet Plans')}
          </h1>
          <p className="mt-6 text-xl text-gray-600">
            {t(
              'diets.subtitle',
              'Designed by clinical nutritionist Paola Michelle. Purchase securely and receive instant access to your complete PDF meal plan, recipes, and grocery list.'
            )}
          </p>
          {checkoutError && (
            <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm inline-block">
              {checkoutError}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
          {diets.map((diet) => {
            const formattedPrice = `€${Number(diet.price_eur ?? 9.99).toFixed(2)}`;
            return (
              <div
                key={diet.id}
                className="bg-white rounded-[2.5rem] shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 overflow-hidden flex flex-col border border-white relative group"
              >
                <div className="p-8 sm:p-10 flex-grow relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                      <ShoppingCart className="w-7 h-7 text-primary" />
                    </div>
                    {diet.badge && (
                      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full bg-amber-100 text-amber-800">
                        <Sparkles className="w-3.5 h-3.5" />
                        {diet.badge}
                      </span>
                    )}
                  </div>

                  <h3 className="text-2xl font-black text-gray-900 mb-3">{diet.title}</h3>
                  <p className="text-gray-500 font-medium mb-8 flex-grow leading-relaxed">
                    {diet.description}
                  </p>

                  <div className="text-4xl font-black text-primary mb-8 px-4 py-2 bg-primary/5 rounded-full inline-block">
                    {formattedPrice}
                  </div>

                  <ul className="space-y-4 mb-4">
                    {(diet.features || []).map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                          <Check className="h-4 w-4 text-green-600" />
                        </div>
                        <span className="text-gray-700 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-8 bg-gray-50/50 mt-auto flex flex-col space-y-3 relative z-10 box-border">
                  <button
                    onClick={() => handlePaymentStripe(diet.id)}
                    disabled={loadingId === diet.id}
                    className="w-full flex items-center justify-center px-6 py-4 text-base font-bold rounded-full text-white bg-gray-900 hover:bg-primary shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 disabled:bg-gray-300 disabled:shadow-none disabled:transform-none"
                  >
                    <Download className="w-5 h-5 mr-3" />
                    {loadingId === diet.id
                      ? t('diets.redirecting', 'Redirecting…')
                      : t('diets.buyNow', 'Buy & Download PDF')}
                  </button>
                  <p className="text-[11px] text-gray-500 text-center leading-snug pt-1">
                    {t(
                      'legal.digitalCheckoutConsent',
                      'Instant PDF delivery via Stripe. By purchasing, you consent to immediate digital access under our'
                    )}{' '}
                    <Link to="/terms" className="text-primary hover:underline font-semibold">
                      {t('legal.termsLink', 'Terms & Refund Policy')}
                    </Link>{' '}
                    ·{' '}
                    <Link
                      to="/medical-disclaimer"
                      className="text-primary hover:underline font-semibold"
                    >
                      {t('legal.medicalDisclaimerLink', 'Medical Disclaimer')}
                    </Link>
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Stripe & GDPR Compliance Trust Strip */}
        <div className="mt-14 bg-white rounded-3xl border border-gray-100 p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs sm:text-sm text-gray-600 shadow-sm">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            <span>
              {t(
                'legal.stripeTrustNotice',
                '100% secure checkout powered by Stripe. All nutritional guides are educational and subject to our Medical Disclaimer & Refund Policy.'
              )}
            </span>
          </div>
          <div className="flex items-center gap-4 font-bold text-primary flex-shrink-0">
            <Link to="/terms" className="hover:underline">
              {t('legal.termsLink', 'Terms & Refunds')}
            </Link>
            <Link to="/privacy" className="hover:underline">
              {t('legal.privacyLink', 'Privacy Policy')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Diets;
